<script lang="ts">
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import FuturePricesStore from "@shared/svelte/Stores/FuturePricesStore.js";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import MoneyFormatters from "@shared/filters/money";
  import { soldInCases } from "@shared/svelte/ProductHelpers.js";
  import { capitalize } from "lodash";
  import { canCalculateOunces, pricePerOunce } from "@retailer/services/unit_calculation_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  export let product: Product;
  export let productLine: ProductLine;
  export let futurePricesData: any = null;
  export let showPricePerOunce: boolean = true;
  export let showPriceNotAvailable: boolean = false;
  export let flexVertical: boolean = false;

  const displayPricePerOunce = showPricePerOunce && stagedRolloutCheck("display_price_per_ounce");

  let futurePrices = futurePricesData || $FuturePricesStore;

  let containerCaseSize = product.container_case_size;
  let containerType = product.container_type;

  let showMinBottle = false;

  $: inventoryItemSoldBy = $InventoryItemStore?.inventoryItems[product.inventory_id]?.sold_by;

  const isSoldInCases = soldInCases(product, productLine, inventoryItemSoldBy);

  function formatMoney(price) {
    return MoneyFormatters.formatWithDecimal(price);
  }

  let caseLevelPrice, casePriceForDisplay, oneBottlePrice;
  $: futurePrices = futurePricesData || $FuturePricesStore;
  $: pricingForProduct = futurePrices.find(p => p.inventory_id === product.inventory_id);
  $: {
    if (pricingForProduct) {
      oneBottlePrice = pricingForProduct.current_prices.find(p => p.bottles == 1);

      if (containerCaseSize && containerCaseSize > 1) {
        caseLevelPrice = pricingForProduct.current_prices.find(p => p.bottles == containerCaseSize);
      }
    }
    casePriceForDisplay = caseLevelPrice || oneBottlePrice;
    showMinBottle = Boolean(oneBottlePrice && caseLevelPrice);
  }
</script>

{#if oneBottlePrice}
  <div>
    <div
      class:fizz-flex-row-4={!flexVertical}
      class:fizz-flex-vertical={flexVertical}
      class:fizz-stack-4={isSoldInCases}
      class:fizz-stack-12={!isSoldInCases}
      data-testid="bottle-price"
      style="flex-wrap: wrap;"
    >
      <div class="fizz-flex-row-4 fizz-max-content">
        <span class="fizz-text-medium span-price-label fizz-text-strong">
          {#if showMinBottle}
            Min bottle:
          {:else}
            {capitalize(containerType)} price:
          {/if}
        </span>

        <span class="fizz-text-medium fizz-text-strong fizz-text-deal">
          {formatMoney(oneBottlePrice.price_per_unit)}
        </span>
      </div>

      {#if canCalculateOunces(product) && displayPricePerOunce}
        <p class="fizz-text-medium fizz-text-deal">
          (${pricePerOunce(product, oneBottlePrice.price_per_unit)}/oz)
        </p>
      {/if}
      {#if oneBottlePrice.discount_per_unit > 0 && !isSoldInCases && !showMinBottle}
        <p
          class="fizz-text-medium fizz-text-strong strikethrough fizz-text-right"
          style={`${flexVertical ? "padding-right:12px" : ""}`}
        >
          {formatMoney(oneBottlePrice.list)}
        </p>
        {#if displayPricePerOunce}
          <p class="fizz-text-medium fizz-text-subdued">
            ({formatMoney(oneBottlePrice.discount_per_unit)} savings)
          </p>
        {/if}
      {/if}
    </div>
    {#if isSoldInCases}
      <div
        class="fizz-stack-12"
        data-testid="case-price"
        class:fizz-flex-row-4={!flexVertical}
        class:fizz-flex-vertical={flexVertical}
        style="flex-wrap: wrap;"
      >
        <div class="fizz-flex-row-4 fizz-flex-no-shrink">
          <p
            class="fizz-text-medium span-price-label fizz-text-strong"
            class:fizz-align-self-start={flexVertical}
          >
            Case price:
          </p>

          <p class="fizz-text-medium fizz-text-strong fizz-text-deal">
            {formatMoney(casePriceForDisplay.price_per_case)}
          </p>
        </div>

        {#if canCalculateOunces(product, true) && displayPricePerOunce}
          <p class="fizz-text-medium fizz-text-deal fizz-flex-no-shrink">
            (${pricePerOunce(product, casePriceForDisplay.price_per_case, true)}/oz)
          </p>
        {/if}

        {#if casePriceForDisplay.discount_per_case > 0}
          <p class="fizz-text-medium fizz-text-strong strikethrough fizz-text-right">
            {formatMoney(casePriceForDisplay.list)}
          </p>

          {#if displayPricePerOunce}
            <p class="fizz-text-medium fizz-text-subdued fizz-flex-no-shrink">
              ({formatMoney(casePriceForDisplay.discount_per_case)} savings) - {formatMoney(
                casePriceForDisplay.price_per_unit,
              )} per {containerType}
            </p>
          {/if}
        {/if}
      </div>
    {/if}
  </div>
{/if}
{#if showPriceNotAvailable && !oneBottlePrice}
  <div data-testid="sku-item-price-not-available">Price not available</div>
{/if}
